import React, { useCallback, useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { Spin, Typography } from 'antd'
import { isEmpty, isNil, map, range } from 'lodash'

import { GridFormItem } from '../../../core/components/sections'
import { showErrorNotification } from '../../../core/helpers'

import { Page, Select } from '../../../common/components/sections'
import { StyledPaddingBottomRow } from '../../../common/components/styled'
import { APP_ROUTES } from '../../../common/constants'
import { useDocumentTitle } from '../../../common/hooks'

import { PRIORITY_TASK_ALLOCATION_TYPE } from '../../constants'
import {
  usePriorityTaskQuery,
  useUpdatePriorityTaskMutation,
} from '../../hooks'
import {
  PriorityTaskMenuCurationCounts,
  PriorityTaskMenuCurationTaskFilters,
  PriorityTaskMenuCurationTaskTable,
} from '../sections'

const PriorityTaskPage = () => {
  const { id } = useParams()
  useDocumentTitle(APP_ROUTES.MISC.children.PRIORITY_TASKS.label)

  const priorityOptions = useMemo(
    () => map(range(1, 5), value => ({ value, label: value })),
    [],
  )

  const {
    data,
    isLoading: isFetchLoading,
    error: fetchError,
  } = usePriorityTaskQuery(id)

  const [update, { isLoading: isUpdateLoading, error: updateError }] =
    useUpdatePriorityTaskMutation()

  useEffect(() => {
    if (fetchError) {
      showErrorNotification({
        title: 'Fetch priority tasks failed',
        description: fetchError,
      })
    }
  }, [fetchError])

  useEffect(() => {
    if (updateError) {
      showErrorNotification({
        title: 'Failed to update',
        description: updateError,
      })
    }
  }, [updateError])

  const handlePriorityChange = useCallback(
    priority => update(id, { priority }),
    [update, id],
  )

  if (fetchError) {
    return <Typography.Text>Failed to get menu curation tasks</Typography.Text>
  }

  if (isFetchLoading || isEmpty(data)) {
    return <Spin />
  }

  if (data.isDone) {
    return (
      <Page>
        <StyledPaddingBottomRow gutter={16}>
          <GridFormItem span={4} label="Name">
            <Typography.Text strong>{data.name}</Typography.Text>
          </GridFormItem>
          <GridFormItem span={4} label="Description">
            <Typography.Text strong>{data.description}</Typography.Text>
          </GridFormItem>
          <GridFormItem span={3} label="Allocation type">
            <Typography.Text strong>
              {PRIORITY_TASK_ALLOCATION_TYPE[data.allocationType]?.label}
            </Typography.Text>
          </GridFormItem>
        </StyledPaddingBottomRow>
        <Typography.Paragraph strong>
          Priority task is done
        </Typography.Paragraph>
      </Page>
    )
  }

  return (
    <Page>
      <StyledPaddingBottomRow gutter={16}>
        <GridFormItem span={4} label="Name">
          <Typography.Text strong>{data.name}</Typography.Text>
        </GridFormItem>
        <GridFormItem span={4} label="Description">
          <Typography.Text strong>{data.description}</Typography.Text>
        </GridFormItem>
        <GridFormItem span={3} label="Allocation type">
          <Typography.Text strong>
            {PRIORITY_TASK_ALLOCATION_TYPE[data.allocationType]?.label}
          </Typography.Text>
        </GridFormItem>
        {!isNil(data.customRequiredDishCount) ? (
          <GridFormItem span={3} label="Required dish count">
            <Typography.Text strong>
              {data.customRequiredDishCount}
            </Typography.Text>
          </GridFormItem>
        ) : (
          <GridFormItem span={2} label="KPI">
            <Typography.Text strong>{data.kpi}</Typography.Text>
          </GridFormItem>
        )}

        <GridFormItem span={2} label="Priority">
          <Select
            options={priorityOptions}
            value={data.priority}
            disabled={isUpdateLoading}
            onChange={handlePriorityChange}
          />
        </GridFormItem>
      </StyledPaddingBottomRow>
      <PriorityTaskMenuCurationTaskFilters priorityTaskId={data?.id} />
      <PriorityTaskMenuCurationCounts priorityTask={data} />

      <PriorityTaskMenuCurationTaskTable priorityTask={data} />
    </Page>
  )
}

export default PriorityTaskPage
